import {
	Card,
	PageHeader,
	SegmentedTabs,
	SegmentedTabList,
	SegmentedTab
} from '@clickguard/design-system'
import { useEffect, useState } from 'react'
import { RiShareBoxFill } from 'react-icons/ri'
import { useStores } from '../../../../../hooks'
import { PageTitle } from '../../../../../utils/page-title'
import { StandardTrackingSection } from './standard-tracking-section'
import { AdvancedTrackingSection } from './advanced-tracking-section'
import { ProductAccountDTO } from 'api-models'
import { getPermissionTier } from 'utils'
import { features } from 'config/features'

const hasAccessToServerSideTracking = (
	account: ProductAccountDTO | null
): boolean => {
	if (!account) return false
	let plan = account?.billing?.subscription?.plan
	if (account?.billing?.subscription?.type === 'TRIAL') {
		plan =
			account?.billing?.subscription?.next_plan ||
			account?.billing?.subscription?.plan
	}

	const tier = getPermissionTier(plan?.tier)
	if (!tier) return false
	if (!features['server_side_tracking']) return false
	if (tier === 'legacy') return !!features['server_side_tracking']['standard']

	return !!features['server_side_tracking'][tier]
}

export const TrackingSectionV2 = () => {
	const {
		assetStore: { asset },
		accountStore: { account }
	} = useStores()

	// Default to ADVANCED if user has access, otherwise STANDARD
	const [trackingMode, setTrackingMode] = useState<'STANDARD' | 'ADVANCED'>(
		() => {
			// Initialize based on feature access
			return hasAccessToServerSideTracking(account) ? 'ADVANCED' : 'STANDARD'
		}
	)

	const hasAccess = hasAccessToServerSideTracking(account)

	// Check if CNAME installation is verified
	const isCnameVerified = asset?.server_side_tracking?.setupCompleted === true

	useEffect(() => {
		// If CNAME is verified, always show ADVANCED
		if (isCnameVerified && hasAccess) {
			setTrackingMode('ADVANCED')
		} else if (hasAccess) {
			setTrackingMode('ADVANCED')
		} else {
			setTrackingMode('STANDARD')
		}
	}, [hasAccess, isCnameVerified])

	const isValidUrl = (urlString: string) => {
		const urlPattern = new RegExp(
			'^(https?:\\/\\/)?' + // validate protocol
				'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
				'((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
				'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
				'(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
				'(\\#[-a-z\\d_]*)?$',
			'i'
		) // validate fragment locator
		return urlPattern.test(urlString)
	}

	return (
		<div className="w-full">
			<PageTitle pageName="Tracking code" />
			<PageHeader
				layout="stacked"
				title="Tracking code"
				subtitle={
					<span className="inline-flex items-center text-t-checkbox-unactive">
						<span className="font-normal">Install our code on&nbsp;</span>
						<span className="inline-flex max-w-[200px] items-center truncate font-bold">
							{asset?.name}
							{asset?.name && isValidUrl(asset.name) && (
								<a
									href={`//${asset.name}`}
									target="_blank"
									rel="noreferrer"
									className="px-1 text-primary-500"
								>
									<RiShareBoxFill />
								</a>
							)}
						</span>
						<span className="font-normal">
							&nbsp;so we can protect your ads and website from ad fraud and
							automated bots.
						</span>
					</span>
				}
			/>
			<div className="pt-4">
				<Card
					padding="none"
					className="w-full !rounded-xl !border-t-traffic !shadow-[0px_4px_8px_0px_rgba(16,24,40,0.03),0px_1px_2px_0px_rgba(16,24,40,0.02)]"
				>
					<div className="flex flex-col gap-4 p-4">
						{!isCnameVerified && (
							<SegmentedTabs
								value={trackingMode}
								onChange={(v) =>
									setTrackingMode(v as 'STANDARD' | 'ADVANCED')
								}
								size="regular"
								className="w-fit"
							>
								<SegmentedTabList>
									<SegmentedTab
										value="STANDARD"
										label="Standard tracking"
									/>
									<SegmentedTab
										value="ADVANCED"
										label="Advanced tracking"
										counter={
											<span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-brand-tint-text">
												PRO
											</span>
										}
									/>
								</SegmentedTabList>
							</SegmentedTabs>
						)}
						<div className={isCnameVerified ? 'mt-4' : ''}>
							{trackingMode === 'STANDARD' && <StandardTrackingSection />}
							{trackingMode === 'ADVANCED' && <AdvancedTrackingSection />}
						</div>
					</div>
				</Card>
			</div>
		</div>
	)
}

export { TrackerCodeTemplate } from './standard-tracking-section'
